// Enhanced SearchPage.js with proper listing integration and promoted priority

import React, { Component } from 'react';
import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';
import { searchAllListings } from './SearchPage.duck';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, ModalInMobile, Page } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768;
const SEARCH_WITH_MAP_DEBOUNCE = 300;
const FILTER_DROPDOWN_OFFSET = -14;

/**
 * Enhanced listing combination and sorting logic - TRULY MIXED
 */
const combineAndSortListings = (sharetribeListings, dynamoDBListings, sortConfig, searchParams, rentalTideListings = []) => {
  // Step 1: Add source metadata to all listings for tracking
  const sharetribeWithSource = sharetribeListings.map(listing => ({
    ...listing,
    _source: 'sharetribe',
    _originalIndex: sharetribeListings.indexOf(listing),
  }));

  const dynamoDBWithSource = dynamoDBListings.map(listing => ({
    ...listing,
    _source: 'dynamodb',
    _originalIndex: dynamoDBListings.indexOf(listing),
  }));

  const rentalTideWithSource = rentalTideListings.map(listing => ({
    ...listing,
    _source: 'rentaltide',
    _originalIndex: rentalTideListings.indexOf(listing),
  }));

  // Step 2: Combine ALL listings into one array
  const allListings = [...sharetribeWithSource, ...dynamoDBWithSource, ...rentalTideWithSource];

  // Step 3: Create unified promotion detection function
  const getPromotionPriority = listing => {
    const publicData = listing.attributes?.publicData || {};

    // Highest priority promotions
    if (publicData.supercap === true) return 100;
    if (publicData.ourPick === true) return 90;

    // High priority promotions
    if (publicData.promote === true) return 80;
    if (publicData.promoted === true) return 75;
    if (publicData.featured === true) return 70;
    if (publicData.priority === 'high') return 65;

    // Medium priority promotions
    if (publicData.priority === 'medium') return 40;
    if (publicData.boost === true) return 35;

    // Regular listings
    return 0;
  };

  // Step 4: Create unified relevance scoring function
  const getRelevanceScore = (listing, keywords) => {
    if (!keywords) return 0;

    const title = listing.attributes?.title?.toLowerCase() || '';
    const description = listing.attributes?.description?.toLowerCase() || '';
    const location = listing.attributes?.publicData?.location?.destination?.toLowerCase() || '';
    const city = listing.attributes?.publicData?.location?.city?.toLowerCase() || '';

    let score = 0;

    // Exact title match gets highest score
    if (title === keywords) score += 50;
    else if (title.includes(keywords)) score += 25;

    // Description matches
    if (description.includes(keywords)) score += 15;

    // Location matches
    if (location.includes(keywords)) score += 20;
    if (city && city.includes(keywords)) score += 10;

    // Partial keyword matches
    const keywordParts = keywords.split(' ');
    keywordParts.forEach(part => {
      if (part.length > 2) {
        // Only count meaningful word parts
        if (title.includes(part)) score += 5;
        if (description.includes(part)) score += 3;
        if (location.includes(part)) score += 4;
      }
    });

    return score;
  };

  // Step 5: Create unified quality scoring function with partner experience emphasis
  const getQualityScore = listing => {
    const publicData = listing.attributes?.publicData || {};
    const isPartner = listing._source === 'dynamodb' || listing._source === 'rentaltide';
    let score = 0;

    // Rating score (0-50 points)
    const rating = parseFloat(publicData.rating || 0);
    score += rating * 10; // 5-star = 50 points

    // Review count boost - HEAVILY weighted for partners
    const reviewCount = parseInt(publicData.reviewCount || publicData.totalRatingCount || 0);
    if (isPartner) {
      // Partner experiences get massive boost for reviews
      score += Math.min(reviewCount / 2, 100); // Up to 100 points for 200+ reviews

      // Extra boost for high-review partners
      if (reviewCount >= 100) score += 50; // Bonus 50 points for 100+ reviews
      if (reviewCount >= 500) score += 75; // Bonus 75 points for 500+ reviews
      if (reviewCount >= 1000) score += 100; // Bonus 100 points for 1000+ reviews
    } else {
      // Regular Sharetribe listings get normal review scoring
      score += Math.min(reviewCount / 10, 20); // Max 20 points for reviews
    }

    // Photo quality boost (0-15 points)
    const photoCount = parseInt(publicData.photoCount || 0);
    score += Math.min(photoCount * 2, 15);

    // Recency boost (0-10 points)
    const lastUpdated = listing.attributes?.publicData?.lastUpdated;
    if (lastUpdated) {
      const daysSinceUpdate =
        (Date.now() - new Date(lastUpdated).getTime()) / (1000 * 60 * 60 * 24);
      score += Math.max(10 - daysSinceUpdate / 30, 0); // Newer = higher score
    }

    // Partner experience bonus
    if (isPartner) {
      score += 25; // Base 25-point bonus for being a partnered experience
    }

    return score;
  };

  // Step 6: Create comprehensive sorting function with partnered experiences priority
  const sortAllListings = (listings, sortBy, keywords) => {
    // Sharetribe's actual sort values: 'price' / '-price' / 'createdAt' /
    // '-createdAt' (- prefix = descending) plus 'pub_promote' / 'pub_supercap'.
    // When the user has explicitly chosen any of these, honor it — don't let
    // promotion priority override their choice.
    const isExplicitSort =
      sortBy === 'price' ||
      sortBy === '-price' ||
      sortBy === 'createdAt' ||
      sortBy === '-createdAt';

    return listings.sort((a, b) => {
      const isPartnerA = a._source === 'dynamodb' || a._source === 'rentaltide';
      const isPartnerB = b._source === 'dynamodb' || b._source === 'rentaltide';
      const reviewsA = parseInt(
        a.attributes?.publicData?.reviewCount || a.attributes?.publicData?.totalRatingCount || 0
      );
      const reviewsB = parseInt(
        b.attributes?.publicData?.reviewCount || b.attributes?.publicData?.totalRatingCount || 0
      );

      // Apply user-chosen sort first when it's explicit.
      if (isExplicitSort) {
        const priceA = a.attributes?.price?.amount || 0;
        const priceB = b.attributes?.price?.amount || 0;
        const createdA = new Date(a.attributes?.createdAt || a.createdAt || 0).getTime();
        const createdB = new Date(b.attributes?.createdAt || b.createdAt || 0).getTime();
        switch (sortBy) {
          case 'price':
            if (priceA !== priceB) return priceA - priceB;
            break;
          case '-price':
            if (priceA !== priceB) return priceB - priceA;
            break;
          case 'createdAt':
            if (createdA !== createdB) return createdA - createdB;
            break;
          case '-createdAt':
            if (createdA !== createdB) return createdB - createdA;
            break;
        }
        // Tie — leave order alone.
        return 0;
      }

      // Default / relevance flow — promotion priority + partner bumps apply.
      const promotionA = getPromotionPriority(a);
      const promotionB = getPromotionPriority(b);
      if (promotionA !== promotionB) {
        return promotionB - promotionA;
      }

      const highReviewThreshold = 50;
      const hasHighReviewsA = isPartnerA && reviewsA >= highReviewThreshold;
      const hasHighReviewsB = isPartnerB && reviewsB >= highReviewThreshold;
      if (hasHighReviewsA && !hasHighReviewsB) return -1;
      if (hasHighReviewsB && !hasHighReviewsA) return 1;
      if (hasHighReviewsA && hasHighReviewsB && reviewsA !== reviewsB) {
        return reviewsB - reviewsA;
      }

      const relevanceA = getRelevanceScore(a, keywords);
      const relevanceB = getRelevanceScore(b, keywords);
      if (relevanceA !== relevanceB) {
        return relevanceB - relevanceA;
      }

      // Fourth: General partner experience boost (DynamoDB with good reviews)
      if (isPartnerA && !isPartnerB && reviewsA >= 10) {
        return -1; // Partner with decent reviews beats non-partner
      }
      if (isPartnerB && !isPartnerA && reviewsB >= 10) {
        return 1; // Partner with decent reviews beats non-partner
      }

      // Fifth: Quality tie-breaker (for same promotion level & sort criteria)
      const qualityA = getQualityScore(a);
      const qualityB = getQualityScore(b);

      if (qualityA !== qualityB) {
        return qualityB - qualityA;
      }

      // Sixth: Review count preference for partners
      if (isPartnerA && isPartnerB) {
        if (reviewsA !== reviewsB) {
          return reviewsB - reviewsA; // More reviews first among partners
        }
      }

      // Seventh: Source balancing (alternate between sources when all else equal)
      const indexA = a._originalIndex || 0;
      const indexB = b._originalIndex || 0;

      // Slight preference for variety in sources
      if (a._source !== b._source) {
        return (indexA + indexB) % 2 === 0 ? -1 : 1;
      }

      // Final: Original order
      return indexA - indexB;
    });
  };

  // Step 7: Sort all listings together
  const keywords = searchParams.keywords?.toLowerCase() || '';
  const sortedListings = sortAllListings(allListings, searchParams.sort, keywords);

  // Step 8: Apply intelligent mixing for better distribution — but ONLY when
  // the user hasn't picked an explicit price/date sort. Mixing reorders
  // promoted listings to the top, which would undo the explicit sort.
  const sortIsExplicit =
    searchParams.sort === 'price' ||
    searchParams.sort === '-price' ||
    searchParams.sort === 'createdAt' ||
    searchParams.sort === '-createdAt';
  const finalListings = sortIsExplicit
    ? sortedListings
    : applyIntelligentMixing(sortedListings);

  // Clean up metadata before returning
  return finalListings.map(listing => {
    const { _source, _originalIndex, ...cleanListing } = listing;
    return cleanListing;
  });
};

/**
 * Apply intelligent mixing to ensure good distribution of sources
 */
const applyIntelligentMixing = sortedListings => {
  const result = [];
  const promotedListings = [];
  const regularListings = [];

  // Helper function to get promotion priority (duplicated for scope)
  const getPromotionPriority = listing => {
    const publicData = listing.attributes?.publicData || {};
    if (publicData.supercap === true) return 100;
    if (publicData.ourPick === true) return 90;
    if (publicData.promote === true) return 80;
    if (publicData.promoted === true) return 75;
    if (publicData.featured === true) return 70;
    if (publicData.priority === 'high') return 65;
    if (publicData.priority === 'medium') return 40;
    if (publicData.boost === true) return 35;
    return 0;
  };

  // Separate promoted from regular
  sortedListings.forEach(listing => {
    if (getPromotionPriority(listing) > 0) {
      promotedListings.push(listing);
    } else {
      regularListings.push(listing);
    }
  });

  // Add promoted listings first (already sorted)
  result.push(...promotedListings);

  // For regular listings, apply smart mixing
  if (regularListings.length > 0) {
    const mixed = smartMixSources(regularListings);
    result.push(...mixed);
  }

  return result;
};

/**
 * Smart mixing algorithm to distribute sources evenly
 */
const smartMixSources = listings => {
  const sharetribeListings = listings.filter(l => l._source === 'sharetribe');
  const partnerListings = listings.filter(l => l._source === 'dynamodb' || l._source === 'rentaltide');

  const result = [];
  let sIdx = 0,
    pIdx = 0;

  // Calculate ratio for mixing (Sharetribe vs all partner sources)
  const totalListings = sharetribeListings.length + partnerListings.length;
  if (totalListings === 0) return result;

  const sharetribeRatio = sharetribeListings.length / totalListings;

  // Mix sources based on their relative quantities
  for (let i = 0; i < totalListings; i++) {
    const shouldTakeSharetribe = i * sharetribeRatio > sIdx - 0.5;

    if (shouldTakeSharetribe && sIdx < sharetribeListings.length) {
      result.push(sharetribeListings[sIdx]);
      sIdx++;
    } else if (pIdx < partnerListings.length) {
      result.push(partnerListings[pIdx]);
      pIdx++;
    } else if (sIdx < sharetribeListings.length) {
      result.push(sharetribeListings[sIdx]);
      sIdx++;
    }
  }

  return result;
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;
    const routes = this.props.routeConfiguration;
    const searchPagePath = pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;
    const isSearchPage = currentPath === searchPagePath;

    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = { listingFieldsConfig, defaultFiltersConfig, listingCategories };

      const { address, bounds, mapSearch, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
      };

      history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams));
    }
  }

  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  applyFilters() {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = { listingFieldsConfig, defaultFiltersConfig, listingCategories };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
  }

  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    this.setState({ currentQueryParams: {} });

    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = { listingFieldsConfig, defaultFiltersConfig, listingCategories };
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};

        return {
          currentQueryParams: omitLimitedListingFieldParams(
            { ...mergedQueryParams, ...updatedURLParams, ...keywordsMaybe, address, bounds },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  render() {
    const {
      intl,
      combinedListings, // This will be the pre-sorted combined listings
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = { listingFieldsConfig, defaultFiltersConfig, listingCategories };

    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = urlQueryParams;
    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));

    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );

    const availablePrimaryFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
    ];
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);
    const selectedFilters = validQueryParams;
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, {
          listingFieldsConfig: customSecondaryFilters,
          defaultFiltersConfig: [],
          listingCategories,
        })
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems = hasPaginationInfo
      ? pagination.totalItems
      : combinedListings.length;

    const listingsAreLoaded =
      !searchInProgress && !!(hasPaginationInfo || combinedListings.length > 0);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };

    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { bounds, origin } = searchParamsInURL || {};
    const { title, description, schema } = createSearchResultSchema(
      combinedListings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.container}>
          <div className={css.searchResultContainer}>
            <SearchFiltersMobile
              className={css.searchFiltersMobileMap}
              urlQueryParams={validQueryParams}
              sortByComponent={sortBy('mobile')}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onMapIconClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
              onManageDisableScrolling={onManageDisableScrolling}
              onOpenModal={this.onOpenMobileModal}
              onCloseModal={this.onCloseMobileModal}
              resetAll={this.resetAll}
              selectedFiltersCount={selectedFiltersCountForMobile}
              noResultsInfo={noResultsInfo}
              isMapVariant
            >
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersMobile"
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                  />
                );
              })}
            </SearchFiltersMobile>

            <MainPanelHeader
              className={css.mainPanelMapVariant}
              sortByComponent={sortBy('desktop')}
              isSortByActive={sortConfig.active}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              noResultsInfo={noResultsInfo}
            >
              <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
                {availablePrimaryFilters.map(filterConfig => {
                  const key = `SearchFiltersPrimary.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersPrimary"
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      showAsPopup
                      contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                    />
                  );
                })}
              </SearchFiltersPrimary>
            </MainPanelHeader>

            {isSecondaryFiltersOpen ? (
              <div className={classNames(css.searchFiltersPanel)}>
                <SearchFiltersSecondary
                  urlQueryParams={validQueryParams}
                  listingsAreLoaded={listingsAreLoaded}
                  applyFilters={this.applyFilters}
                  cancelFilters={this.cancelFilters}
                  resetAll={this.resetAll}
                  onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                >
                  {customSecondaryFilters.map(filterConfig => {
                    const key = `SearchFiltersSecondary.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersSecondary"
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersSecondary>
              </div>
            ) : (
              <div
                className={classNames(css.listingsForMapVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={combinedListings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  setActiveListing={onActivateListing}
                  isMapVariant
                />
              </div>
            )}
          </div>
          <ModalInMobile
            className={css.mapPanel}
            id="SearchPage.map"
            isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.mapWrapper} data-testid="searchMapContainer">
              {shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.map}
                  activeListingId={activeListingId}
                  bounds={bounds}
                  center={origin}
                  isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                  location={location}
                  listings={combinedListings || []}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {
                    onManageDisableScrolling('SearchPage.map', false);
                  }}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          </ModalInMobile>
        </div>
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  combinedListings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  activeListingId: null,
};

SearchPageComponent.propTypes = {
  combinedListings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  history: shape({ push: func.isRequired }).isRequired,
  location: shape({ search: string.isRequired }).isRequired,
  intl: intlShape.isRequired,
  config: object.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

/**
 * Enhanced mapStateToProps with proper listing combination and sorting
 */
const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    dynamoDBListings = [],
    dynamoDBSearchInProgress = false,
    dynamoDBSearchError = null,
    dynamoDBMeta = { totalItems: 0 },
    rentalTideListings = [],
    rentalTideSearchInProgress = false,
    rentalTideSearchError = null,
    rentalTideMeta = { totalItems: 0 },
  } = state.SearchPage;

  // Get Sharetribe listings
  const sharetribeListings = getListingsById(state, currentPageResultIds);

  // Use the enhanced combination logic with all three sources
  const combinedListings = combineAndSortListings(
    sharetribeListings,
    dynamoDBListings,
    state.config?.search?.sortConfig || {},
    searchParams || {},
    rentalTideListings
  );

  // Calculate combined pagination info
  const sharetribeTotalItems = pagination?.totalItems || 0;
  const dynamoDBTotalItems = dynamoDBMeta?.totalItems || 0;
  const rentalTideTotalItems = rentalTideMeta?.totalItems || 0;
  const combinedTotalItems = sharetribeTotalItems + dynamoDBTotalItems + rentalTideTotalItems;

  const combinedPagination = pagination
    ? {
        ...pagination,
        totalItems: combinedTotalItems,
        totalPages: Math.ceil(combinedTotalItems / (pagination.perPage || 96)),
      }
    : null;

  return {
    combinedListings,
    pagination: combinedPagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError: searchListingsError || dynamoDBSearchError || rentalTideSearchError,
    searchParams,
    activeListingId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
  onSearchAllListings: (searchParams, config) => dispatch(searchAllListings(searchParams, config)),
});

const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
